/**
 * Afterpay Variant Fix
 * Ensures Afterpay components stay visible and update correctly when variants change
 */
class AfterPayVariantFix {
  constructor() {
    this.reinitTimeout = null;
    this.init();
  }

  init() {
    // Wait for Afterpay to load before setting up listeners
    this.waitForAfterPay(() => {
      this.setupVariantListeners();
      this.setupFallbackObserver();
    });
  }

  waitForAfterPay(callback) {
    // Check if Afterpay is available
    if (window.AfterPay || window.Afterpay || window.afterpay) {
      callback();
    } else {
      // Wait for Afterpay script to load
      setTimeout(() => this.waitForAfterPay(callback), 100);
    }
  }

  setupVariantListeners() {
    // Listen for variant changes
    document.addEventListener('variant:change', (event) => {
      this.handleVariantChange(event.detail);
    });

    // Listen for price updates (fallback)
    document.addEventListener('price:update', () => {
      this.reinitializeAfterPay();
    });

    // Listen for any DOM changes in price areas
    const priceElements = document.querySelectorAll('[data-product-price], .product__price, [data-price-wrapper]');
    priceElements.forEach(element => {
      if (element) {
        this.observeElementChanges(element);
      }
    });
  }

  setupFallbackObserver() {
    // Create a mutation observer for the entire product form area
    const productForm = document.querySelector('[data-product-form]') || 
                       document.querySelector('.product-form') ||
                       document.querySelector('.product__price');
    
    if (productForm) {
      const observer = new MutationObserver((mutations) => {
        let shouldReinit = false;
        
        mutations.forEach(mutation => {
          // Check if price-related elements were modified
          if (mutation.type === 'childList') {
            const addedNodes = Array.from(mutation.addedNodes);
            const removedNodes = Array.from(mutation.removedNodes);
            
            [...addedNodes, ...removedNodes].forEach(node => {
              if (node.nodeType === Node.ELEMENT_NODE) {
                if (node.classList && (
                  node.classList.contains('product__price') ||
                  node.hasAttribute && node.hasAttribute('data-product-price') ||
                  node.hasAttribute && node.hasAttribute('data-price-wrapper') ||
                  node.querySelector && node.querySelector('[data-product-price], [data-price-wrapper], .product__price')
                )) {
                  shouldReinit = true;
                }
              }
            });
          }
          
          // Check if price attributes were modified
          if (mutation.type === 'attributes' && 
              (mutation.attributeName === 'data-product-price' || 
               mutation.attributeName === 'data-price-wrapper' ||
               mutation.target.classList.contains('product__price'))) {
            shouldReinit = true;
          }
        });
        
        if (shouldReinit) {
          this.reinitializeAfterPay();
        }
      });
      
      observer.observe(productForm, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-product-price', 'data-price-wrapper', 'class']
      });
    }
  }

  observeElementChanges(element) {
    const observer = new MutationObserver(() => {
      this.reinitializeAfterPay();
    });
    
    observer.observe(element, {
      childList: true,
      characterData: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-product-price', 'data-price-wrapper']
    });
  }

  handleVariantChange(variantData) {
    // Add a small delay to ensure DOM updates are complete
    this.reinitializeAfterPay(200);
  }

  reinitializeAfterPay(delay = 100) {
    // Clear any existing timeout to prevent excessive calls
    if (this.reinitTimeout) {
      clearTimeout(this.reinitTimeout);
    }
    
    this.reinitTimeout = setTimeout(() => {
      this.performReinit();
    }, delay);
  }

  performReinit() {
    try {
      // Method 1: Try window.AfterPay (newer versions)
      if (window.AfterPay && typeof window.AfterPay.display === 'object') {
        if (typeof window.AfterPay.display.refresh === 'function') {
          window.AfterPay.display.refresh();
          console.log('AfterPay refreshed via display.refresh()');
          return;
        }
        if (typeof window.AfterPay.display.init === 'function') {
          window.AfterPay.display.init();
          console.log('AfterPay reinitialized via display.init()');
          return;
        }
      }

      // Method 2: Try window.Afterpay (alternative casing)
      if (window.Afterpay && typeof window.Afterpay.display === 'object') {
        if (typeof window.Afterpay.display.refresh === 'function') {
          window.Afterpay.display.refresh();
          console.log('Afterpay refreshed via display.refresh()');
          return;
        }
      }

      // Method 3: Try global afterpay function
      if (typeof window.afterpay === 'function') {
        window.afterpay();
        console.log('AfterPay reinitialized via global function');
        return;
      }

      // Method 4: Try to find and re-trigger Afterpay elements
      this.refreshAfterPayElements();

    } catch (error) {
      console.warn('AfterPay reinit error:', error);
      // Fallback: try to refresh elements manually
      this.refreshAfterPayElements();
    }
  }

  refreshAfterPayElements() {
    // Find all afterpay elements and try to re-trigger them
    const afterpayElements = document.querySelectorAll('[data-afterpay], .afterpay-paragraph, .afterpay');
    
    afterpayElements.forEach(element => {
      // Try to re-trigger Afterpay on this element
      if (element.style.display === 'none' || !element.innerHTML.trim()) {
        // Element is hidden or empty, try to restore it
        element.style.display = '';
        
        // Dispatch a custom event that Afterpay might listen to
        element.dispatchEvent(new Event('afterpay:refresh', { bubbles: true }));
      }
    });

    // Force a repaint
    document.body.offsetHeight;
    
    console.log('AfterPay elements manually refreshed');
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  new AfterPayVariantFix();
});

// Also initialize on page load (for themes with AJAX navigation)
window.addEventListener('load', function() {
  new AfterPayVariantFix();
});

// Re-initialize on section loads (for Shopify theme editor)
document.addEventListener('shopify:section:load', function() {
  new AfterPayVariantFix();
});

// Export for debugging
window.AfterPayVariantFix = AfterPayVariantFix;